/* eslint-disable no-console */

import _ from 'underscore';
import $ from 'jquery';
import ko from 'knockout';
import { TokenInfoFetcher } from '@bingads-webui/token-info-fetcher';
import { keepAliveInstance } from '@bingads-webui/keep-alive';
import { AutoRefreshToken } from '@bingads-webui/auto-refresh-token';
import componentConfigImport from '@bingads-webui-component-legacy/config/index';
import PageContext from '@bingads-webui-static-legacy/page-context';
import { versionCheckInstance } from '@bingads-webui-campaign/version-check';
import { PageReady } from '@bingads-webui/page-ready';
import { setupQualtricsWithData } from '@bingads-webui-campaign/qualtrics';
import { setupClarity } from '@bingads-webui-universal/clarity';
import { PerfMarkerFactory } from '@bingads-webui/performance-marker';
import { PreferenceService } from '@bingads-webui/preference-service';
import { GetJsDataInfo } from '@bingads-webui-clientcenter/js-data-cc-rest-base';
import { isSDK } from '@bingads-webui-campaign-react/sdk-utils';
import { redirectToLogin } from './constants';
import { initPDL } from './omniture';
import { initWalkMe } from './walk-me';

const PROD_CLARITY_ID = '4jwlwd615c';
const TEST_CLARITY_ID = '6vo4jbalnn';

const CookieConsentActivityName = 'wcp-consent';

function getJsData(serverContext) {
  const cid = _.chain(serverContext).result('CurrentCustomer').result('Id').value();
  const uid = _.chain(serverContext).result('CurrentUser').result('Id').value();
  const clientCenterBaseUrl = _.chain(serverContext).result('ServicesEndpoints').result('ClientCenterUIAPI').value();

  const options = {
    cidFetcher: () => cid,
    uidFetcher: () => uid,
    appendErrorSummary: _.noop,
    handleAjaxAuthFailure: _.noop,
    willSupportCors: true,
  };

  return GetJsDataInfo(options, clientCenterBaseUrl, []).jsDataCcRest;
}

function initOCVSurvey(serverContext, appActivity) {
  const activityName = 'survey-management';
  const isEnhancedSurveyTriggerConditionEnabled = _.result(serverContext, ['DynamicPermissions', 'EnhancedSurveyTriggerCondition']);

  const appPerfMarker = PerfMarkerFactory.createMarker('Survey_Management');
  appPerfMarker.willInit();
  const tokenFetchPerfMarker = appPerfMarker.createChild('Token_Fetch');
  tokenFetchPerfMarker.willInit();

  import(/* webpackMode: 'eager' */'@bingads-webui-campaign-react/survey-manager').then(({ setupSurveyManager }) => {
    const ccBaseUrl = _.chain(serverContext).result('Urls').result('ClientCenterBaseUrl').value();
    const IsAADUser = _.chain(serverContext).result('IsAADUser').value();
    const sessionId = _.chain(serverContext).result('Omniture').result('SessionId').value();
    const uid = _.chain(serverContext).result('CurrentUser').result('Id').value();
    const tokenInfoFetcher = new TokenInfoFetcher({
      ccBaseUrl,
      IsAADUser,
      sessionId,
      appActivity,
      shouldDoAjaxCall: true,
      activityName,
    });
    const ccJsData = getJsData(serverContext);

    const fetchTokenInfo = () => new Promise((resolve, reject) => (
      tokenInfoFetcher.getTokenInfo()
        .then((tokenInfo) => {
          tokenFetchPerfMarker.done();
          return resolve(tokenInfo);
        })
        .catch((error) => {
          tokenFetchPerfMarker.done();
          return reject(error);
        })
    ));

    const shouldFetchUser = isEnhancedSurveyTriggerConditionEnabled && !_.result(serverContext, ['CurrentUser', 'CreatedDateTime']);

    Promise.all([
      fetchTokenInfo(),
      shouldFetchUser ? ccJsData.User.find(uid) : Promise.resolve({}),
    ])
      .then(([tokenInfo, userData]) => {
        const { CreatedDateTime } = userData || {};
        const clonedServerContext = _.clone(serverContext);
        if (CreatedDateTime && _.isString(CreatedDateTime) && clonedServerContext && clonedServerContext.CurrentUser) {
          clonedServerContext.CurrentUser.FetchedCreatedDateTime = CreatedDateTime;
          if (shouldFetchUser) {
            clonedServerContext.CurrentUser.CreatedDateTime = CreatedDateTime;
          }
        }

        const initSurveyManagerPerfMarker = appPerfMarker.createChild('Init_Survey_Manager');
        initSurveyManagerPerfMarker.willInit();
        setupSurveyManager({
          i18n: window.globalLegacyI18n.main,
          PageContext,
          serverContext: clonedServerContext,
          PreferenceService,
          tokenInfo,
          appActivity,
          activityName,
          appPerfMarker,
        });
        window.survey.surveyManager.trigger();
        initSurveyManagerPerfMarker.done();
      })
      .catch((error) => {
        if (appActivity) {
          appActivity.trace(`OCV survey initialization promise failed ${JSON.stringify(error)}`, activityName);
        }
      });
  });
}

export function initGlobalService(serverContext = {}, appActivity = {}) {
  try {
    // start keep alive
    keepAliveInstance.unAuthCallBack = () => redirectToLogin();
    keepAliveInstance.start();

    // knockout config
    // below override is needed as we don't want ko.cleanNode to clean event bindings
    // this is a compatibility patch when we upgrade jquery to 1.9.1
    ko.utils.domNodeDisposal.cleanExternalData = () => {}; // eslint-disable-line no-param-reassign

    // start version check
    versionCheckInstance.version = serverContext.Version;
    versionCheckInstance.start();

    // init an empty function to avoid customer click the help icon
    // before the apex loading is ready
    window.PopupController = { PopupJsClick: () => {} };

    PageReady.afterPageReady().then(() => {
      initWalkMe(serverContext);
      setupQualtricsWithData(serverContext, {
        PreferenceService,
        enableIframeCommunication: true,
        appActivity,
      });

      if (!isSDK()) {
        initOCVSurvey(serverContext, appActivity);
      }

      const init1DSAnalytics = () => {
        const scriptUrl = 'https://js.monitor.azure.com/scripts/c/ms.analytics-web-3.min.js';

        if (!_.isFunction($.ajax)) {
          return new Promise();
        }

        return new Promise((resolve) => {
          $.ajax({
            url: scriptUrl,
            dataType: 'script',
            crossOrigin: true,
            credentials: 'omit',
          }).always(() => resolve());
        });
      };

      const initAdobeTracking = () => {
        const trackingScriptUrl = _.chain(serverContext).result('ThirdPartConfig').result('AdobeTrackingScriptUrl', undefined).value();
        if (!_.isUndefined(trackingScriptUrl) && _.isFunction($.ajax)) {
          $.ajax({
            url: trackingScriptUrl,
            dataType: 'script',
            crossOrigin: true,
            credentials: 'omit',
            success: () => initPDL(serverContext),
          });
        }
      };

      const initAmplifyTracking = () => {
        const amplifyTrackingScriptUrl = _.chain(serverContext).result('AmplifyTrackingScriptUrl', undefined).value();
        if (!_.isUndefined(amplifyTrackingScriptUrl) && _.isFunction($.ajax)) {
          $.ajax({
            url: amplifyTrackingScriptUrl,
            dataType: 'script',
            crossOrigin: true,
            credentials: 'omit',
          });
        }
      };

      const initClarity = () => {
        setupClarity(
          serverContext.IsProdEnv ? PROD_CLARITY_ID : TEST_CLARITY_ID,
          {
            customerId: serverContext.CurrentCustomer.Id.toString(),
            accountId: serverContext.CurrentAccount
              && serverContext.CurrentAccount.Id
              && serverContext.CurrentAccount.Id.toString(),
            SessionId: serverContext.Omniture.SessionId,
            PageTrackingId: serverContext.PageTrackingId,
          },
          serverContext.CurrentUser.Id.toString(),
          serverContext.Omniture.SessionId,
          serverContext.PageTrackingId
        );
      };

      const script = document.createElement('script');
      script.src = 'https://wcpstatic.microsoft.com/mscc/lib/v2/wcp-consent.js';
      script.async = true;
      script.onload = () => {
        // Init WcpConsent before in init clarity
        // cookie-banner will be hidden after user sign in, we only use this element to init WCP
        // we depend on CCUI page to show cookie banner to users
        if (window.WcpConsent) {
          appActivity.trace('WcpConsent: initialize start', CookieConsentActivityName);
          window.WcpConsent.init(serverContext.CurrentLocale, 'cookie-banner', (err, _siteConsent) => {
            appActivity.trace('WcpConsent: initialize complete', CookieConsentActivityName);
            if (!err) {
              window.siteConsent = _siteConsent;
              appActivity.trace('WcpConsent: initialize succeed', CookieConsentActivityName);

              if (!window.WcpConsent
                  || !window.WcpConsent.siteConsent
                  || !window.WcpConsent.siteConsent.onConsentChanged
                  || !window.WcpConsent.siteConsent.getConsent
              ) {
                if (appActivity && appActivity.error) {
                  appActivity.error('WcpConsent: WcpConsent component is not initialized. Cannot read user cookie consent info');
                }
                return;
              }

              appActivity.trace('WcpConsent: read cookie consent value to see if we can init clarity', CookieConsentActivityName);

              const initAnalytics = ({ Analytics, Advertising }) => {
                if (Analytics) {
                  init1DSAnalytics()
                    .then(() => initAdobeTracking());

                  initAmplifyTracking();

                  if (Advertising) {
                    initClarity();
                  }
                }
              };

              const currentConsent = window.WcpConsent.siteConsent.getConsent();

              if (currentConsent.Analytics) {
                initAnalytics(currentConsent);
              } else {
                window.WcpConsent.siteConsent.onConsentChanged(initAnalytics);
              }
            } else {
              appActivity.error(`WcpConsent: initialize error: ${err}`);
            }
          });
        } else {
          appActivity.trace('WcpConsent: wcp-consent.js script has not been loaded yet', CookieConsentActivityName);
        }
      };
      script.onerror = () => {
        // ad blocker might block this script, so we only log trace here.
        appActivity.trace('WcpConsent: WcpConsent script didn\'t load correctly.');
      };

      document.head.appendChild(script);

      const autoRefreshTokenInstance = new AutoRefreshToken({
        serverContext,
        PageContext,
        config: componentConfigImport,
        appActivity,
      });

      autoRefreshTokenInstance.start();
    });
  } catch (e) {
    console.error(`init global service raise exception ${e}`);
    appActivity.trace(`init global service raise exception ${e}`);
  }
}
